import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LocationSearch } from "@/components/LocationSearch";
import { BackLink } from "@/components/BackLink";
import { Req, ERR_CLS } from "@/components/RequiredMark";
import { Seo } from "@/components/Seo";
import { HeartHandshake, CheckCircle2, Users, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PostHelpRequest() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [when, setWhen] = useState("");
  const [hours, setHours] = useState(1);
  const [minTrust, setMinTrust] = useState(0); // 18-B1: min trust for offers — off by default
  const [saving, setSaving] = useState(false);
  const [posted, setPosted] = useState(null); // created doc incl. transient reach counts
  const [errors, setErrors] = useState({});
  const clearErr = (k) => setErrors((prev) => (prev[k] ? { ...prev, [k]: false } : prev));

  if (user?.user_type === "organisation") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <h1 className="font-display text-2xl text-forest">Help requests are for individuals</h1>
        <p className="text-neutral-600 mt-2">Organisations can post volunteering opportunities from the dashboard instead.</p>
      </div>
    );
  }

  // Post-success reassurance screen (Phase 11): honest reach numbers from the
  // create response — "visible" mirrors the nearby feed, "notified" the users
  // who opted into local alerts. Zero nearby gets encouragement, not a zero.
  if (posted) {
    const reach = posted.reach || { visible: 0, notified: 0 };
    const area = (posted.location || "").split(",")[0].trim();
    const reachLine =
      reach.visible > 0 && reach.notified > 0
        ? `${reach.visible} neighbour${reach.visible === 1 ? "" : "s"} near ${area} can see your request — ${reach.notified === reach.visible ? (reach.visible === 1 ? "and they were" : "and they were all") : `${reach.notified} of them ${reach.notified === 1 ? "was" : "were"}`} alerted right away.`
        : reach.visible > 0
          ? `${reach.visible} neighbour${reach.visible === 1 ? "" : "s"} near ${area} can see your request on their dashboard.`
          : "Your request is live — we'll surface it to neighbours as they join near you.";
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card className="border-sky-200 bg-sky-50" data-testid="help-success-card">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-sky-600 mx-auto" />
            <h1 className="font-display text-3xl text-forest mt-4 tracking-tight">Your request is live</h1>
            <p className="text-neutral-700 mt-4 inline-flex items-start gap-2 text-left max-w-md" data-testid="help-success-reach">
              <Users className="w-4 h-4 text-sky-700 mt-1 shrink-0" />
              <span>{reachLine}</span>
            </p>
            <p className="text-sm text-neutral-500 mt-3">
              You'll get a notification the moment someone offers to help — and you choose who it is.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <Button data-testid="help-success-view-btn" onClick={() => nav(`/opportunities/${posted.id}`)} className="rounded-full bg-sky-600 hover:bg-sky-700 h-11 px-6">
                View your request <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button data-testid="help-success-dashboard-btn" variant="outline" onClick={() => nav("/dashboard")} className="rounded-full h-11 px-6 border-forest/20 text-forest">
                Back to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }


  const submit = async () => {
    const missing = {
      title: !title.trim(),
      description: !description.trim(),
      when: !when,
      location: !location.trim(),
    };
    setErrors(missing);
    if (Object.values(missing).some(Boolean)) return toast.error("Fill the required fields — highlighted in red");
    setSaving(true);
    try {
      const created = await api.createOpportunity({
        title, description, location, lat, lng, remote: false,
        hours_required: Number(hours) || 1,
        event_date: new Date(when).toISOString(),
        values: [], skills_needed: [],
        is_help_request: true,
        min_trust_level: Number(minTrust) || 0,
      });
      if (created.geo_unresolved) toast.warning("We couldn't pin that location on the map, so nearby neighbours won't be alerted automatically. Edit the request and pick a suggested location to fix this.");
      setPosted(created);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Seo title="Ask a neighbour for help" description="Post a help request on CivicHearth — a neighbour can collect your shopping, take the bins out, or lend a hand." />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-sky-700 font-bold inline-flex items-center gap-1.5">
        <HeartHandshake className="w-4 h-4" /> Neighbourly help
      </div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Ask a neighbour for help</h1>
      <p className="text-neutral-600 mt-3 max-w-xl">
        Need someone to collect your shopping, take the bins out, or walk the dog while you're unwell?
        Post a request — nearby volunteers will offer, and <span className="text-sky-700 font-medium">you choose who helps</span>.
      </p>

      <Card className="mt-8 border-neutral-200">
        <CardContent className="p-8 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">What do you need help with?<Req /></label>
            <Input data-testid="help-title-input" value={title} onChange={(e) => { setTitle(e.target.value); clearErr("title"); }} placeholder="e.g. Collect my weekly shopping" className={`mt-2 h-11 ${errors.title ? ERR_CLS : ""}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">A few details<Req /></label>
            <Textarea data-testid="help-desc-input" value={description} onChange={(e) => { setDescription(e.target.value); clearErr("description"); }} rows={4} placeholder="What needs doing, anything the helper should know, and roughly how long it'll take." className={`mt-2 ${errors.description ? ERR_CLS : ""}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Where — area or postcode<Req /></label>
            <div className="mt-2">
              <LocationSearch
                testId="help-location-input"
                value={location}
                placeholder="e.g. Walthamstow, London or E17 4PP"
                inputClassName={errors.location ? ERR_CLS : ""}
                onSelect={(r) => { setLocation(r.label); setLat(r.lat); setLng(r.lng); clearErr("location"); }}
                onTextChange={(v) => { setLocation(v); setLat(null); setLng(null); clearErr("location"); }}
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1.5">Pick a suggestion so nearby volunteers get alerted. Your exact address stays private — share it only with your chosen helper.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">When do you need it?<Req /></label>
              <Input data-testid="help-when-input" type="datetime-local" value={when} onChange={(e) => { setWhen(e.target.value); clearErr("when"); }} className={`mt-2 h-11 ${errors.when ? ERR_CLS : ""}`} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Rough time needed (hours)</label>
              <Input data-testid="help-hours-input" type="number" min={0.5} step={0.5} value={hours} onChange={(e) => setHours(e.target.value)} className="mt-2 h-11" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Who can offer?
            </label>
            <select
              data-testid="help-min-trust-select"
              value={minTrust}
              onChange={(e) => setMinTrust(Number(e.target.value))}
              className="mt-2 h-11 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
            >
              <option value={0}>Anyone can offer (default)</option>
              <option value={1}>Active neighbours+ — complete profile with approved hours or help</option>
              <option value={2}>Community-vouched+ — endorsed by neighbours or vouched by a leader</option>
              <option value={3}>DBS seen only — an organisation has told us they've seen their DBS certificate</option>
            </select>
            <p className="text-xs text-neutral-500 mt-1.5">
              For sensitive tasks you can limit offers to neighbours with a higher trust level. You still choose who helps either way.{" "}
              <Link to="/safeguarding" data-testid="help-safeguarding-link" className="text-forest underline underline-offset-2">
                How we keep neighbours safe
              </Link>
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button data-testid="help-submit-btn" onClick={submit} disabled={saving} className="rounded-full bg-sky-600 hover:bg-sky-700 h-11 px-6">
              {saving ? "Posting…" : "Post help request"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
